import { useTradeMenu } from '@src/containers/Blotters/Trades/TradeMenu';
import {
  MultilegView,
  colIDToFilterBuilderKey,
  type BlotterTableTradeFilter,
  type UseTradeFilterOutput,
} from '@src/containers/Blotters/Trades/useTradeFilter';
import { getTradingOrderDetailsRoute } from '@src/containers/Routes/routes';
import type { GenerateOrderDetailsRoute } from '@src/containers/Trading/Markets/OrderDetails/types';
import { useFeatureFlag, useUser } from '@src/hooks';
import { useDisplaySettings, useTradingSettings } from '@src/providers/AppConfigProvider';
import { useContextBlotterFilter } from '@src/providers/ContextBlotterFilterProvider/useContextBlotterFilter';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import { exportOMSTrades } from '@src/utils/blotterQueries';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  ControlPrefix,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  EMPTY_OBJECT,
  ExportDialog,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  Portal,
  SearchSelect,
  SummaryLine,
  Toggle,
  WSBlotterTableMaxRecordsReachedWarning,
  createExportFileName,
  filterByColumnMainMenuItems,
  formattedDateForSubscription,
  getStringLabel,
  getTradeAmount,
  getTradeQuantity,
  getTypedKeys,
  isGridApiReady,
  parseDate,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useDisclosure,
  useMixpanel,
  useWsBlotterTable,
  type ExportGridMode,
  type Trade,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams, SelectionChangedEvent } from 'ag-grid-enterprise';
import { isEmpty } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { toTradesServerFilter } from './serverFilter';
import { TRADES_MULTILEG_VIEW_CONTAINER_ID } from './tokens';
import { useFilteredTradesBlotter } from './useFilteredTradesBlotter';

interface FilteredTradesParams {
  /** ID for common blotter type persistence */
  blotterPersistID: string;

  tabLabel?: string;

  orderID?: string;

  defaultColumns?: Parameters<typeof useFilteredTradesBlotter>[0]['defaultColumns'];
  defaultFilter: BlotterTableTradeFilter;
  onCloneTab?: () => void;
  initialIsOpen?: boolean;

  showAPIOrdersToggle?: boolean;
  generateOrderDetailsRoute?: GenerateOrderDetailsRoute;
  showOpenOrderDetails?: boolean;
  persistFilter?: boolean;

  showAllDecimals?: boolean;
  setShowAllDecimals?: (value: boolean) => void;
  /** Layout ID, currently only used for opening order details in the trading layout */
  layoutId: string | undefined;
}

export const FilteredTrades = function FilteredTrades({
  blotterPersistID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  orderID,
  onCloneTab,
  initialIsOpen,
  showAPIOrdersToggle,
  generateOrderDetailsRoute = getTradingOrderDetailsRoute,
  showOpenOrderDetails,
  persistFilter,
  showAllDecimals,
  setShowAllDecimals,
  layoutId,
}: FilteredTradesParams) {
  const mixpanel = useMixpanel();
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const user = useUser();

  const { filterPropsMutator, additionalFilterState } = useContextBlotterFilter<UseTradeFilterOutput<Trade>>();
  // only show DateRangePicker if it's not used in the additionalFilterState
  const showDateRangePicker = !(additionalFilterState && 'DateRange' in additionalFilterState);

  const { subAccountsByName } = useSubAccounts();
  const { homeCurrency } = useDisplaySettings();

  const [selectedTrades, setSelectedTrades] = useState<Trade[]>([]);
  const { density, rowID, columns, persistedBlotterTable, filteredTrades, request, startingRowLimit } =
    useFilteredTradesBlotter({
      blotterPersistID,
      defaultFilter,
      defaultColumns,
      orderID,
      persistFilter,
      filterByOrderID: !!orderID,
      additionalFilterState,
      blotterTagPrefix: 'FILTERED_TRADES',
    });
  const {
    clientSideFilter: clientLocalFilter,
    blotterTableFilterProps,
    changeFilter,
    filterBuilderProps,
    filterableProperties,
  } = useMemo(() => filterPropsMutator(filteredTrades), [filterPropsMutator, filteredTrades]);

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const { openClause } = filterBuilderAccordion;

  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<Trade>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    const selectedRows = api.getSelectedRows();
    setSelectedTrades(selectedRows);
  }, []);

  const exportDialog = useDisclosure();

  const getExtraMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams) => {
      return filterByColumnMainMenuItems({
        params,
        colIDToFilterBuilderKey,
        openClause,
        mixpanel,
      });
    },
    [mixpanel, openClause]
  );
  const { getCustomContextMenuItems, dialogs } = useTradeMenu({
    openClause,
    filterableProperties,
    generateOrderDetailsRoute,
    showOpenOrderDetails,
    layoutId,
  });

  const handleExportTrades = useCallback(() => {
    mixpanel.track(MixpanelEvent.ExportTrades);
    const serverFilter = {
      ...(toTradesServerFilter(filteredTrades.filter) ?? {}),
      ...(orderID != null && { OrderID: orderID }),
    };
    const stringFilters = getTypedKeys(serverFilter)
      .map(key => {
        const value = serverFilter[key];
        if (isEmpty(value)) {
          return undefined;
        }
        switch (key) {
          case 'StartDate':
          case 'EndDate':
            return undefined;
          case 'Symbols':
            return `Symbol:${value as (typeof serverFilter)[typeof key]}`;
          case 'SubAccounts':
            // Map sub account Names to their IDs to make this API happy
            return `SubAccountID:${(value as NonNullable<(typeof serverFilter)[typeof key]>)
              .map(subAcctName => subAccountsByName?.get(subAcctName)?.SubaccountID || undefined)
              .compact()}`;
        }
        return `${key}:${value}`;
      })
      .compact();
    const query = {
      startDate: formattedDateForSubscription(serverFilter.StartDate),
      endDate: formattedDateForSubscription(serverFilter.EndDate || parseDate(null)),
      orderBy: '-TransactTime',
      filter: stringFilters.length > 0 ? stringFilters : undefined,
    };
    if (!query.startDate) {
      // Backend doesn't like an empty startDate, but is fine if we delete it
      delete query.startDate;
    }
    exportOMSTrades(user.OrgApiEndpoint, query);
  }, [mixpanel, filteredTrades.filter, orderID, user.OrgApiEndpoint, subAccountsByName]);

  const { showBlotterPauseButton, enableMultilegTradeFilter } = useFeatureFlag();

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const blotterTable = useWsBlotterTable({
    request,
    persistence: persistedBlotterTable,
    rowID,
    density,
    clientLocalFilter,
    columns,
    getExtraMainMenuItems,
    contextMenuOptions: { getCustomContextMenuItems },
    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    startingRowLimit,
    settings: blotterTableSettings,
    gridOptions: {
      onSelectionChanged: handleRowSelectionChanged,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
    },
    exportOptions: {
      csvParams: EMPTY_OBJECT,
      excelParams: EMPTY_OBJECT,
      name: 'Trades',
      tabLabel: tabLabel,
    },
  });

  const handleExport = useCallback(
    (mode: ExportGridMode) => {
      const exportFunc = mode === 'CSV' ? blotterTable.exportDataAsCSV : blotterTable.exportDataAsExcel;
      exportFunc({
        fileName: createExportFileName({
          name: 'Trades',
          tabLabel,
          mode,
        }),
      });
    },
    [blotterTable.exportDataAsCSV, blotterTable.exportDataAsExcel, tabLabel]
  );

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleOnApiOrdersChange = useCallback(
    (value: boolean) => {
      mixpanel.track(MixpanelEvent.ApiOrdersToggle, { [MixpanelEventProperty.Enabled]: value });
      changeFilter(curr => ({ ...curr, HideApiCalls: !value }));
    },
    [changeFilter, mixpanel]
  );

  const handleOnOffPlatformTradesChange = useCallback(
    (value: boolean) => {
      mixpanel.track(MixpanelEvent.OffPlatformTradesToggle, { [MixpanelEventProperty.Enabled]: value });
      changeFilter(curr => ({ ...curr, HideOffPlatform: !value }));
    },
    [changeFilter, mixpanel]
  );

  const multilegViewOptions = useMemo(() => [MultilegView.Parent, MultilegView.Child, MultilegView.Both], []);

  const handleMultilegViewChange = useCallbackRef((view: MultilegView | undefined) => {
    if (view != null) {
      changeFilter(curr => ({ ...curr, MultilegView: view }));
    }
  });

  return (
    <>
      {enableMultilegTradeFilter && (
        <Portal portalId={TRADES_MULTILEG_VIEW_CONTAINER_ID}>
          <SearchSelect
            options={multilegViewOptions}
            selection={filteredTrades.filter.MultilegView ?? MultilegView.Both}
            getLabel={getStringLabel}
            onChange={handleMultilegViewChange}
            size={FormControlSizes.Small}
            showDropdownSearch={false}
            portalize
            dropdownWidth="90px"
            prefix={<ControlPrefix>Multileg Trades</ControlPrefix>}
            data-testid="multileg-view-select"
          />
        </Portal>
      )}
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        showDateRangePicker={showDateRangePicker}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            {showAPIOrdersToggle && (
              <>
                <Toggle
                  label="API Orders"
                  size={FormControlSizes.Small}
                  tooltip="Include trades from orders submitted through the API in the results."
                  checked={!filteredTrades.filter.HideApiCalls}
                  onChange={handleOnApiOrdersChange}
                />
                <Toggle
                  label="Off Platform Trades"
                  size={FormControlSizes.Small}
                  tooltip="Display Off Platform Trades in the Trade Blotter"
                  checked={!filteredTrades.filter.HideOffPlatform}
                  onChange={handleOnOffPlatformTradesChange}
                />
              </>
            )}
            {!showAllDecimalsInBlotters && setShowAllDecimals != null && (
              <Toggle
                label="Show All Decimals"
                size={FormControlSizes.Small}
                checked={showAllDecimals ?? false}
                onChange={setShowAllDecimals}
              />
            )}
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}

            <Button
              startIcon={IconName.DocumentDownload}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              data-testid="export-csv"
              onClick={() => {
                mixpanel.track(MixpanelEvent.ExportRowsModalOpen);
                exportDialog.open();
              }}
            >
              Export
            </Button>
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning
        {...blotterTable.paginationLimit}
        getTimestamp={trade => trade.TransactTime}
      />
      <BlotterTable {...blotterTable} rowEndSpacing="spacing12" />

      <SummaryLine
        homeCurrency={homeCurrency}
        rows={selectedTrades}
        getQuantity={getTradeQuantity}
        getAmount={getTradeAmount}
      />
      <ExportDialog
        {...exportDialog}
        handleExportLocalData={handleExport}
        handleExportServerData={handleExportTrades}
      />
      {dialogs}
    </>
  );
};
